/**
 * ConfirmCard — renders the LLM's parsed result. The user reviews and edits
 * before tapping Save. NEVER auto-saves. (UX-AC-021 / AC-080.)
 *
 * Per UX §3.2: header row with serif h3 + tiny status text ("matched" / "needs
 * review" / "new place"); two-column compact list of parsed fields; notes
 * shown in serif italic with hairline left rule (editorial pull-quote
 * treatment); secondary "Edit fields" + primary save button whose label
 * varies by confidence and place-match state per UX §4.8.
 */
import * as React from 'react';
import type {
  CategoryId,
  ParsedIntent,
  ParsedIntentItem,
  PlaceId,
} from '@shared/types';
import { Badge } from './ui/Badge';
import { Button } from './ui/Button';
import { DotRating, type DotValue } from './ui/DotRating';
import { TextArea } from './ui/TextArea';
import { TextInput } from './ui/TextInput';
import { FormField } from './ui/FormField';
import { TagChip } from './ui/TagChip';
import { formatTimelineDate } from '../lib/date';
import { cn } from '../lib/cn';

interface EditableItem extends ParsedIntentItem {
  /** Per-row React key; not sent to the server. */
  _key: string;
}

function toEditable(items: ParsedIntentItem[]): EditableItem[] {
  return items.map((i, idx) => ({
    name: i.name,
    rating: i.rating,
    notes: i.notes,
    _key: `item-${idx}-${Math.random().toString(36).slice(2, 8)}`,
  }));
}

function fromEditable(items: EditableItem[]): ParsedIntentItem[] {
  return items
    .map((i) => ({
      name: i.name.trim(),
      rating: i.rating === 0 || i.rating === null ? null : i.rating,
      notes: i.notes && i.notes.trim().length > 0 ? i.notes.trim() : null,
    }))
    .filter((i) => i.name.length > 0);
}

export type ConfirmCardMatchState = 'matched' | 'needs-review' | 'new-place';

export interface ConfirmCardCategory {
  id: CategoryId;
  name: string;
}

export interface ConfirmCardCandidate {
  id: PlaceId;
  name: string;
  /** Optional secondary line (category, neighborhood, etc.) shown under the name. */
  hint?: string;
}

export interface ConfirmCardProps {
  parsed: ParsedIntent;
  /** The matched place's name, if any. */
  matchedPlaceName?: string;
  matchedPlaceId?: PlaceId;
  /** 0..1 confidence from the parse response. */
  confidence: number;
  /**
   * The user-confirmed save callback. Backend re-validates the full
   * ParsedIntent shape — the card flushes any local edits into the
   * `confirmed` field before invoking.
   */
  onSave: (payload: {
    confirmed: ParsedIntent;
    place_id?: PlaceId | 'new';
    new_place?: { name: string; category_id: CategoryId };
  }) => void;
  onCancel: () => void;
  loading?: boolean;
  /** Categories for the create-new-place case. */
  categories?: ConfirmCardCategory[];
  /**
   * Optional household-wide place candidates surfaced when the model's
   * confidence in the place match is very low (AC-082). When provided and
   * confidence < 0.5, the card renders a combobox so the user explicitly
   * picks "match this existing place" vs "create a new one" instead of
   * silently defaulting to create-new.
   */
  candidates?: ConfirmCardCandidate[];
}

const LOW_CONF = 0.75;
const COULDNT_TELL = 0.45;

function deriveMatchState(opts: {
  matchedPlaceId?: PlaceId;
  confidence: number;
}): ConfirmCardMatchState {
  if (opts.matchedPlaceId) {
    return opts.confidence >= LOW_CONF ? 'matched' : 'needs-review';
  }
  return 'new-place';
}

function saveLabelFor(state: ConfirmCardMatchState, confidence: number): string {
  if (state === 'new-place') return 'Create place & save';
  if (state === 'needs-review' || confidence < LOW_CONF) return 'Confirm & save';
  return 'Save this entry';
}

export function ConfirmCard({
  parsed,
  matchedPlaceName,
  matchedPlaceId,
  confidence,
  onSave,
  onCancel,
  loading,
  categories = [],
  candidates = [],
}: ConfirmCardProps) {
  const initialMatch = deriveMatchState({
    matchedPlaceId,
    confidence,
  });

  // Editable local state (only flushed to onSave).
  const [editing, setEditing] = React.useState(false);
  const [placeName, setPlaceName] = React.useState(
    matchedPlaceName ?? parsed.place.name ?? '',
  );
  const [categoryId, setCategoryId] = React.useState<CategoryId | ''>(
    parsed.place.category_id ?? '',
  );
  const [address, setAddress] = React.useState(parsed.place.address ?? '');
  const [visitedAt, setVisitedAt] = React.useState(parsed.visit.visited_at);
  const [rating, setRating] = React.useState<DotValue>(
    (parsed.visit.rating as DotValue | null) ?? 0,
  );
  const [notes, setNotes] = React.useState(parsed.visit.notes ?? '');
  // Tag editing is deferred to Round 3 (full tag combobox). For Round 2 we
  // surface the parsed tags as a read-only chip strip.
  const [tags] = React.useState<string[]>(parsed.tags ?? []);
  // Items — editable list of dishes / rooms / work performed. Local state
  // only; flushed onto `confirmed.visit.items` when the user taps Save.
  const [items, setItems] = React.useState<EditableItem[]>(() =>
    toEditable(parsed.visit.items ?? []),
  );

  function addItem() {
    setItems((prev) => [
      ...prev,
      {
        name: '',
        rating: null,
        notes: null,
        _key: `item-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      },
    ]);
  }
  function updateItem(key: string, patch: Partial<EditableItem>) {
    setItems((prev) =>
      prev.map((it) => (it._key === key ? { ...it, ...patch } : it)),
    );
  }
  function removeItem(key: string) {
    setItems((prev) => prev.filter((it) => it._key !== key));
  }

  // AC-082: when the parser's confidence is very low AND we have existing
  // places to suggest, surface a combobox so the user explicitly picks "this
  // is one of these" or "no, make a new one." `pickedCandidate` overrides
  // matchedPlaceId on save.
  const showCandidatePicker = confidence < COULDNT_TELL && candidates.length > 0;
  const [pickedCandidate, setPickedCandidate] =
    React.useState<PlaceId | null>(null);

  const matchState: ConfirmCardMatchState =
    initialMatch === 'new-place'
      ? 'new-place'
      : editing
        ? initialMatch
        : initialMatch;

  const reviewBadge: { variant: 'neutral' | 'accent' | 'warn'; label: string } =
    matchState === 'matched'
      ? { variant: 'neutral', label: 'Matched' }
      : matchState === 'new-place'
        ? { variant: 'accent', label: 'New place' }
        : { variant: 'warn', label: 'Needs review' };

  const couldntTell = confidence < COULDNT_TELL;

  const saveLabel = saveLabelFor(matchState, confidence);

  // When the candidate picker is required, the user MUST either pick a
  // candidate or explicitly type a new name; we cannot guess.
  const saveDisabled =
    (couldntTell && matchState !== 'matched' && !placeName.trim()) ||
    (showCandidatePicker && pickedCandidate === null && !placeName.trim());

  function handleSave() {
    // Backend re-validates the FULL ParsedIntent shape — merge local edits
    // into the parsed object and send the complete `confirmed` payload.
    const confirmed: ParsedIntent = {
      ...parsed,
      place: {
        ...parsed.place,
        name: placeName.trim(),
        category_id: categoryId || null,
        address: address.trim() || null,
      },
      visit: {
        ...parsed.visit,
        visited_at: visitedAt,
        rating: rating === 0 ? null : rating,
        notes: notes.trim() || null,
        items: fromEditable(items),
      },
      tags,
      people: parsed.people,
    };

    // Picker selection wins over any inferred match — the user has made an
    // explicit choice.
    const effectivePlaceId = pickedCandidate ?? matchedPlaceId;

    if (effectivePlaceId) {
      onSave({ confirmed, place_id: effectivePlaceId });
    } else {
      onSave({
        confirmed,
        place_id: 'new',
        new_place: {
          name: placeName.trim(),
          // When category not set, the caller's apply mutation should surface
          // a validation error and we surface it back via toast.
          category_id: (categoryId || categories[0]?.id) as CategoryId,
        },
      });
    }
  }

  return (
    <section
      role="region"
      aria-labelledby="confirm-title"
      className={cn(
        'bg-surface border border-ink-200',
        'p-6 md:p-8 mt-6',
        couldntTell && 'border-warn',
      )}
    >
      <header className="flex items-start justify-between gap-4 pb-4 border-b border-ink-100">
        <div className="min-w-0">
          <h3
            id="confirm-title"
            className="font-serif text-2xl text-ink-900 leading-tight"
          >
            {placeName || 'New place'}
          </h3>
          <p className="mt-1 text-xs uppercase tracking-[0.14em] text-ink-500">
            {confidence < COULDNT_TELL
              ? "We weren't sure"
              : matchState === 'matched'
                ? 'Pulled together from your text'
                : matchState === 'new-place'
                  ? 'A place we don\'t have yet'
                  : 'Please confirm'}
          </p>
        </div>
        <Badge variant={reviewBadge.variant}>{reviewBadge.label}</Badge>
      </header>

      {showCandidatePicker ? (
        <div className="mt-5" data-testid="confirm-place-picker">
          <p className="text-[0.65rem] uppercase tracking-[0.14em] text-ink-500 mb-3">
            Which place is this?
          </p>
          <ul className="divide-y divide-ink-100 border border-ink-200 mb-4">
            {candidates.map((c) => {
              const active = pickedCandidate === c.id;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => {
                      setPickedCandidate(c.id);
                      setPlaceName(c.name);
                    }}
                    aria-pressed={active}
                    className={cn(
                      'block w-full text-left px-4 py-3',
                      'transition-colors duration-200 ease-out-slow',
                      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-accent',
                      active
                        ? 'bg-accent-soft text-accent'
                        : 'bg-transparent text-ink-900 hover:bg-surface-2',
                    )}
                  >
                    <span className="font-serif text-base">{c.name}</span>
                    {c.hint ? (
                      <span className="ml-2 text-xs italic text-ink-500">
                        {c.hint}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
          <FormField
            label="…or a new place"
            htmlFor="cn-cf-picker-new"
            optional
          >
            <TextInput
              id="cn-cf-picker-new"
              value={pickedCandidate === null ? placeName : ''}
              onChange={(e) => {
                setPickedCandidate(null);
                setPlaceName(e.target.value);
              }}
              placeholder="Name a new place"
            />
          </FormField>
        </div>
      ) : null}

      {!editing ? (
        <div className="mt-5 grid gap-x-8 gap-y-4 sm:grid-cols-2">
          <FieldRow label="Date" value={visitedAt ? formatTimelineDate(visitedAt) : '—'} />
          <FieldRow
            label="Rating"
            value={
              rating > 0 ? <DotRating value={rating} readOnly size="sm" /> : '—'
            }
          />
          <FieldRow
            label="Tags"
            value={
              tags.length ? (
                <span className="flex flex-wrap gap-1.5">
                  {tags.map((t) => (
                    <TagChip key={t} label={t} />
                  ))}
                </span>
              ) : (
                '—'
              )
            }
          />
          <FieldRow
            label="People"
            value={
              parsed.people.length
                ? parsed.people.map((p) => p.name).join(', ')
                : '—'
            }
          />
          {notes ? (
            <div className="sm:col-span-2 pt-2">
              <p className="text-[0.65rem] uppercase tracking-[0.14em] text-ink-500 mb-2">
                Notes
              </p>
              <blockquote className="font-serif italic text-ink-700 pl-4 border-l border-ink-200 leading-relaxed">
                {notes}
              </blockquote>
            </div>
          ) : null}
        </div>
      ) : (
        <div className="mt-5 grid gap-6 sm:grid-cols-2">
          <FormField label="Place" htmlFor="cn-cf-place">
            <TextInput
              id="cn-cf-place"
              value={placeName}
              onChange={(e) => setPlaceName(e.target.value)}
              placeholder="Place name"
            />
          </FormField>
          <FormField label="Location" htmlFor="cn-cf-address">
            <TextInput
              id="cn-cf-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="City, neighborhood, or full address"
            />
          </FormField>
          {!matchedPlaceId ? (
            <FormField label="Category">
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value as CategoryId)}
                className="block w-full bg-transparent border-0 border-b border-ink-300 px-0 py-3 font-sans text-base text-ink-900 focus:border-b-2 focus:border-accent focus:outline-none"
              >
                <option value="">Pick a category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </FormField>
          ) : null}
          <FormField label="Date" htmlFor="cn-cf-date">
            <TextInput
              id="cn-cf-date"
              type="date"
              value={visitedAt.slice(0, 10)}
              onChange={(e) => setVisitedAt(`${e.target.value}T12:00:00Z`)}
            />
          </FormField>
          <FormField label="Rating">
            <DotRating
              value={rating}
              onChange={(v) => setRating(v)}
              size="md"
            />
          </FormField>
          <FormField label="Notes" htmlFor="cn-cf-notes" className="sm:col-span-2">
            <TextArea
              id="cn-cf-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              minRows={3}
              placeholder="Quoted from your text — edit as you like."
            />
          </FormField>
        </div>
      )}

      {/* Items — editable list of dishes / rooms / work performed. Shown in
          both read-only and editing modes (since items are the differentiator
          vs Yelp, they get pride of place even when not in form-mode). */}
      <section
        className="mt-8 pt-6 border-t border-ink-100"
        aria-labelledby="confirm-items-heading"
        data-testid="confirm-items-section"
      >
        <div className="mb-3 flex items-baseline justify-between">
          <h4
            id="confirm-items-heading"
            className="text-[0.65rem] uppercase tracking-[0.14em] text-ink-500"
          >
            Items
          </h4>
          {items.length === 0 ? (
            <span className="font-serif italic text-xs text-ink-500">
              None mentioned.
            </span>
          ) : null}
        </div>
        {items.length > 0 ? (
          <ul className="divide-y divide-ink-100">
            {items.map((it) => (
              <li
                key={it._key}
                className="py-4 first:pt-0 last:pb-0"
                data-testid="confirm-item-row"
              >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-6">
                  <div className="flex-1 min-w-0">
                    <TextInput
                      value={it.name}
                      onChange={(e) =>
                        updateItem(it._key, { name: e.target.value })
                      }
                      placeholder="Dish, room, or work performed"
                      aria-label="Item name"
                    />
                    <TextArea
                      value={it.notes ?? ''}
                      onChange={(e) =>
                        updateItem(it._key, { notes: e.target.value })
                      }
                      minRows={1}
                      placeholder="What stood out about this one"
                      aria-label="Item notes"
                      className="mt-2"
                    />
                  </div>
                  <div className="flex items-center justify-between gap-3 sm:flex-col sm:items-end sm:gap-2">
                    <DotRating
                      value={(it.rating ?? 0) as DotValue}
                      onChange={(v) => updateItem(it._key, { rating: v })}
                      size="sm"
                    />
                    <button
                      type="button"
                      onClick={() => removeItem(it._key)}
                      aria-label={`Remove ${it.name || 'item'}`}
                      className="text-ink-500 hover:text-error-muted text-xs uppercase tracking-widest focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="mt-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={addItem}
            type="button"
            data-testid="confirm-add-item"
          >
            + Add item
          </Button>
        </div>
      </section>

      <div className="mt-8 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="text-xs uppercase tracking-widest text-ink-500 hover:text-ink-900 focus-visible:outline focus-visible:outline-1 focus-visible:outline-accent"
        >
          Cancel
        </button>
        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            onClick={() => setEditing((e) => !e)}
            disabled={loading}
          >
            {editing ? 'Done editing' : 'Edit fields'}
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            loading={loading}
            loadingLabel="Saving…"
            disabled={saveDisabled || loading}
          >
            {saveLabel}
          </Button>
        </div>
      </div>
    </section>
  );
}

function FieldRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-[0.65rem] uppercase tracking-[0.14em] text-ink-500 min-w-[5rem]">
        {label}
      </span>
      <span className="text-sm text-ink-900">{value}</span>
    </div>
  );
}
